import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useDashboard } from '../context/DashboardContext';
import DailyWidget from '../components/dashboard/DailyWidget';
import WeeklySummary from '../components/dashboard/WeeklySummary';
import DailyInsights from '../components/dashboard/DailyInsights';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../components/layout/DashboardLayout';
import { createEmployeeNavSections } from '../components/layout/dashboardNavConfigs';

const EmployeeDashboard: React.FC = () => {
  const location = useLocation();
  const { isDark } = useTheme();
  const { loadingDashboard, errorDashboard, refreshDashboard } = useDashboard();
  const { t } = useTranslation();

  useEffect(() => {
    refreshDashboard();
    const interval = setInterval(refreshDashboard, 30000);
    return () => clearInterval(interval);
  }, [refreshDashboard]);

  const isActive = (href: string) => {
    if (href === '/employee-dashboard')
      return location.pathname === '/employee-dashboard';
    return location.pathname.startsWith(href);
  };

  const sections = createEmployeeNavSections(t, isActive);

  const bgColor = isDark ? 'bg-slate-900' : 'bg-slate-50';
  const textColor = isDark ? 'text-white' : 'text-slate-900';

  const dashboardContent = (
    <div className={`${bgColor} min-h-screen`}>
      {/* Header */}
      <div className="mb-8">
        <div
          className={`border-b ${isDark ? 'bg-slate-900/50 border-slate-700/50' : 'bg-white/80 border-slate-200'} backdrop-blur-sm sticky top-0 z-10`}
        >
          <div className="px-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 py-5">
              <div>
                <h1
                  className={`text-xl sm:text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}
                >
                  {t('dashboard.title')}
                </h1>
                <p
                  className={`mt-0.5 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                >
                  {t('dashboard.subtitle')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Error State */}
        {errorDashboard && (
          <div className="mb-6 p-4 bg-red-100 border border-red-300 rounded-lg text-red-700">
            {errorDashboard}
          </div>
        )}

        {/* Loading State */}
        {loadingDashboard && (
          <div
            className={`text-center py-12 rounded-lg border-2 border-dashed ${isDark ? 'border-slate-700' : 'border-slate-300'}`}
          >
            <div className={`${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              {t('dashboard.loading')}
            </div>
          </div>
        )}

        {/* Main Grid */}
        {!loadingDashboard && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
            {/* Left Column: Daily + Insights */}
            <div className="lg:col-span-2 space-y-6">
              <DailyWidget />
              <DailyInsights />
            </div>

            {/* Right Column: Weekly Summary */}
            <div>
              <WeeklySummary />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <DashboardLayout sections={sections}>{dashboardContent}</DashboardLayout>
  );
};

export default EmployeeDashboard;
